import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

const INITIAL_CALL = -1 as const;

export type Loader<T> = (continuation: string | undefined) => Promise<{
  continuation?: string;
  items?: readonly T[];
}>;
export type RenderItems<T> = (items: readonly T[]) => React.ReactNode;

class InfiniteScrollStore<T> {
  @observable.ref items: readonly T[] = [];
  @observable.ref continuation: string | typeof INITIAL_CALL | undefined =
    INITIAL_CALL;
  @observable.ref isLoading = false;
  @observable.ref error: unknown = undefined;

  constructor(readonly loader: Loader<T>) {
    makeObservable(this);
  }

  get shouldLoadMore() {
    return !this.isLoading && !this.error && this.continuation != null;
  }

  async loadMore() {
    if (!this.shouldLoadMore) return;
    this.isLoading = true;
    try {
      const res = await this.loader(
        this.continuation == INITIAL_CALL ? undefined : this.continuation,
      );
      if (res.items) this.items = [...this.items, ...res.items];
      this.continuation = res.continuation;
    } catch (e) {
      this.error = e;
      console.error('Infinite load catch error', e);
    } finally {
      this.isLoading = false;
    }
  }
}

/** Renders an infinitely scrolling list of items */
export const InfiniteScroll = observer(function _InfinteScroll<T>({
  loader,
  render,
  LoadingState,
  ErrorState,
}: {
  loader: Loader<T>;
  render: RenderItems<T>;
  LoadingState: React.ComponentType;
  ErrorState: React.ComponentType<{ error: unknown }>;
}) {
  const [store, setStore] = React.useState(
    () => new InfiniteScrollStore(loader),
  );
  React.useEffect(
    function recreateStore() {
      if (store.loader !== loader) setStore(new InfiniteScrollStore(loader));
    },
    [loader],
  );

  const observerRef = React.useRef<IntersectionObserver | undefined>();
  const refSentinal = React.useCallback(
    (el: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      if (el == null) return;

      const obs = new IntersectionObserver((entries) => {
        if (entries.some((x) => x.isIntersecting)) {
          store.loadMore();
        }
      });
      obs.observe(el);
      observerRef.current = obs;

      const rect = el.getBoundingClientRect();
      if (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth &&
        rect.width > 0 &&
        rect.height > 0
      ) {
        store.loadMore();
      }
    },
    [store],
  );

  return (
    <>
      {store.continuation !== INITIAL_CALL && render(store.items)}
      {store.isLoading && <LoadingState />}
      {store.error && <ErrorState error={store.error} />}
      {store.shouldLoadMore && (
        <div style={{ width: 1, height: 1, minHeight: 1 }} ref={refSentinal} />
      )}
    </>
  );
});
